import { useState } from 'react';
import { ChevronDown, Menu } from "lucide-react";
import { AnimatedText, AnimatedTitle } from '../components/ui/AnimatedText';
import { experiencesData } from '../utils/experiencesData';
import { useSEO } from "@/utils/useSEO";
import { useTranslation } from "react-i18next";

const ExperiencesMenu = () => {
  const { t } = useTranslation();
  useSEO(
    "Experiences & Activities",
    "Discover curated activities and experiences in Sidemen, Bali. From Mount Agung trekking and white water rafting to Balinese cooking classes, ATV adventures, and cultural walking tours — all arranged for you by Patal Kikian Villas."
  );
  const [activeCategory, setActiveCategory] = useState(experiencesData[0].title);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleCategoryChange = (title: string) => {
    setActiveCategory(title);
    window.scrollTo({
      top: window.innerHeight * 0.5,
      behavior: 'smooth'
    });
  };

  const scrollToMenu = () => {
    const element = document.getElementById('menu-section');
    if (element) {
      window.scrollTo({
        top: element.getBoundingClientRect().top + window.scrollY - 80,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-[100dvh] bg-background text-white selection:bg-gold/30 selection:text-gold font-sans flex flex-col">

      {/* Hero Section */}
      <section className="relative min-h-[100dvh] w-full flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="/media/patal-kikian-hero-banner.webp"
            alt="Patal Kikian Experiences"
            className="w-full h-full object-cover opacity-85"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
        </div>
        
        <div className="relative z-10 text-center px-4 mt-20">
          <AnimatedText delay={0.1}>
            <span className="text-gold uppercase tracking-[0.2em] text-xs font-semibold mb-4 block">{t("experiences.hero.subtitle", "Things To Do")}</span>
          </AnimatedText>
          <AnimatedTitle title={t("experiences.hero.title", "Create Your Best Memories in Bali")} className="text-4xl md:text-5xl lg:text-6xl font-luxury mb-6 text-shadow-lg leading-tight" />
          <AnimatedText delay={0.3}>
            <p className="text-white/80 max-w-3xl mx-auto text-lg leading-relaxed">
              {t("experiences.hero.desc", "From breathtaking nature adventures to immersive cultural experiences, we present a curated selection of activities to make every moment of your holiday even more special.")}
            </p>
          </AnimatedText>
        </div>

        {/* Scroll Down Button */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20">
          <button 
            onClick={scrollToMenu}
            className="text-white/70 hover:text-white transition-colors duration-300 flex flex-col items-center gap-2 cursor-pointer"
            aria-label="Scroll to experiences"
          >
            <span className="uppercase tracking-[0.2em] text-[10px]">{t("experiences.hero.btnScroll", "View Activities")}</span>
            <ChevronDown size={24} className="animate-bounce" />
          </button>
        </div>
      </section>

      {/* Main Menu Section */}
      <section id="menu-section" className="py-16 md:py-24 bg-background relative z-20 flex-grow">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl flex flex-col md:flex-row gap-12 relative">
          
          {/* Sidebar Navigation (Desktop) */}
          <div className="md:w-1/4 md:sticky md:top-32 self-start hidden md:block">
            <h3 className="text-xl font-luxury mb-6 text-white border-b border-white/10 pb-4">{t("experiences.sidebar.categories", "Categories")}</h3>
            <ul className="space-y-3">
              {experiencesData.map((category) => (
                <li key={category.title}>
                  <button
                    onClick={() => handleCategoryChange(category.title)}
                    className={`text-left w-full transition-colors duration-300 ${
                      activeCategory === category.title ? 'text-gold font-medium' : 'text-white/60 hover:text-white'
                    }`}
                  >
                    {t(`experiences.categories.${category.title}`, { defaultValue: category.title })}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Mobile Category Dropdown */}
          <div className="md:hidden sticky top-24 z-40 bg-background/95 backdrop-blur-md border-b border-white/10 -mx-4 px-4 py-3">
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="w-full flex justify-between items-center text-white text-sm uppercase tracking-wider py-2"
            >
              <span>{t("experiences.sidebar.moreActivities", "More Activities")}</span>
              <Menu className="w-5 h-5 text-gold" />
            </button>
            {isMobileMenuOpen && (
              <div className="flex flex-col gap-4 mt-4 max-h-[50vh] overflow-y-auto pb-4 custom-scrollbar">
                {experiencesData.map((category) => (
                  <button
                    key={category.title}
                    onClick={() => { handleCategoryChange(category.title); setIsMobileMenuOpen(false); }}
                    className={`text-left text-sm uppercase tracking-wider transition-colors duration-300 ${
                      activeCategory === category.title ? 'text-gold' : 'text-white/60'
                    }`}
                  >
                    {t(`experiences.categories.${category.title}`, { defaultValue: category.title })}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Content (Only active category) */}
          <div className="md:w-3/4">
            {experiencesData.filter(c => c.title === activeCategory).map((category) => (
              <div key={category.title} className="mb-20 animate-fade-in">
                <div className="mb-10 text-center md:text-left">
                  <h2 className="text-3xl md:text-4xl font-luxury text-gold mb-2">{t(`experiences.categories.${category.title}`, { defaultValue: category.title })}</h2>
                  <div className="w-16 h-px bg-gold/50 mx-auto md:mx-0 mt-6" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                  {category.items.map((item, idx) => (
                    <div key={idx} className="group cursor-pointer">
                      <div className="aspect-[4/3] w-full overflow-hidden rounded-lg mb-6 relative">
                        <img 
                          src={item.image} 
                          alt={item.title} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
                        />
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-500" />
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <h4 className="text-xl font-medium text-white group-hover:text-gold transition-colors duration-300">{t(`experiences.items.${item.title}.title`, { defaultValue: item.title })}</h4>
                        
                        <p className="text-white/60 text-sm leading-relaxed">{t(`experiences.items.${item.title}.description`, { defaultValue: item.description })}</p>
                        
                        <a 
                          href={`https://wa.me/6281353278504?text=${encodeURIComponent(`Hello, I would like to book the "${item.title}" experience at Patal Kikian Villas.`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 inline-flex items-center text-xs font-semibold uppercase tracking-widest text-gold hover:text-white transition-colors"
                        >{t("experiences.items.bookActivity", "Book Activity")}</a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>
      
    </div>
  );
};

export default ExperiencesMenu;
